import type { LanguageCode } from './languages'

export type TranslationKey =
  | 'nav.home'
  | 'nav.rooms'
  | 'nav.spa'
  | 'nav.restaurant'
  | 'nav.qr'
  | 'hero.eyebrow'
  | 'hero.title'
  | 'hero.copy'
  | 'hero.reserve'
  | 'hero.call'
  | 'booking.title'
  | 'booking.dates'
  | 'booking.rooms'
  | 'booking.adults'
  | 'booking.children'
  | 'booking.submit'
  | 'sections.experience'
  | 'sections.qrTitle'
  | 'sections.subscribe'
  | 'footer.address'
  | 'admin.title'
  | 'admin.subtitle'
  | 'admin.qr'
  | 'admin.integrations'

type TranslationMap = Record<TranslationKey, string>

const source: TranslationMap = {
  'nav.home': 'Ana Sayfa',
  'nav.rooms': 'Kahvalti',
  'nav.spa': 'Sicak Icecekler',
  'nav.restaurant': 'Restaurant',
  'nav.qr': 'QR',
  'hero.eyebrow': 'Yalova Termal Kahvalti & Cafe',
  'hero.title': 'Modi Cafe',
  'hero.copy':
    'Taze kahvalti, sicak kahve, tatli ve sakin bir Yalova Termal molasi tek adreste.',
  'hero.reserve': 'Menuyu Incele',
  'hero.call': 'Hemen Ara',
  'booking.title': 'Masa ve Siparis Iletisimi',
  'booking.dates': 'Ziyaret Tarihi',
  'booking.rooms': 'Kisi Sayisi',
  'booking.adults': 'Yetiskin',
  'booking.children': 'Cocuk',
  'booking.submit': 'WhatsApp ile Iletisime Gec',
  'sections.experience': 'Cafe Deneyimi',
  'sections.qrTitle': 'Cafe Menuleri',
  'sections.subscribe': 'Kampanyalardan Haberdar Ol',
  'footer.address': 'Yalova Termal, Turkiye',
  'admin.title': 'Modi Cafe Admin',
  'admin.subtitle': 'QR, slider, ceviri ve entegrasyon ayarlari icin yonetim paneli.',
  'admin.qr': 'QR Linkleri',
  'admin.integrations': 'Entegrasyonlar',
}

const automatic: Record<Exclude<LanguageCode, 'tr'>, TranslationMap> = {
  en: {
    'nav.home': 'Home',
    'nav.rooms': 'Breakfast',
    'nav.spa': 'Hot Drinks',
    'nav.restaurant': 'Restaurant',
    'nav.qr': 'QR',
    'hero.eyebrow': 'Breakfast & Cafe in Yalova Thermal',
    'hero.title': 'Modi Cafe',
    'hero.copy':
      'Fresh breakfast, warm coffee, desserts and a calm Yalova Thermal break in one place.',
    'hero.reserve': 'View Menu',
    'hero.call': 'Call Now',
    'booking.title': 'Table & Order Contact',
    'booking.dates': 'Visit Date',
    'booking.rooms': 'Guest Count',
    'booking.adults': 'Adults',
    'booking.children': 'Children',
    'booking.submit': 'Contact on WhatsApp',
    'sections.experience': 'Cafe Experience',
    'sections.qrTitle': 'Cafe Menus',
    'sections.subscribe': 'Get Campaign Updates',
    'footer.address': 'Yalova Thermal, Turkey',
    'admin.title': 'Modi Cafe Admin',
    'admin.subtitle':
      'Management panel for QR, slider, translation and integration settings.',
    'admin.qr': 'QR Links',
    'admin.integrations': 'Integrations',
  },
  ru: {
    'nav.home': 'Главная',
    'nav.rooms': 'Завтраки',
    'nav.spa': 'Горячие напитки',
    'nav.restaurant': 'Ресторан',
    'nav.qr': 'QR',
    'hero.eyebrow': 'Кафе и завтраки в Ялова Термал',
    'hero.title': 'Modi Cafe',
    'hero.copy':
      'Свежие завтраки, кофе, десерты и спокойная атмосфера Ялова Термал в одном месте.',
    'hero.reserve': 'Меню',
    'hero.call': 'Позвонить',
    'booking.title': 'Поиск бронирования',
    'booking.dates': 'Заезд и выезд',
    'booking.rooms': 'Номера',
    'booking.adults': 'Взрослые',
    'booking.children': 'Дети',
    'booking.submit': 'Забронировать',
    'sections.experience': 'Проживание',
    'sections.qrTitle': 'Меню кафе',
    'sections.subscribe': 'Получать акции',
    'footer.address': 'Ялова Термал, Турция',
    'admin.title': 'Modi Cafe Admin',
    'admin.subtitle': 'Панель управления QR, переводами и интеграциями.',
    'admin.qr': 'QR-ссылки',
    'admin.integrations': 'Интеграции',
  },
}

const manualOverrides: Partial<Record<LanguageCode, Partial<TranslationMap>>> = {
  en: {},
  ru: {},
}

export const translate = (key: TranslationKey, language: LanguageCode) => {
  if (language === 'tr') {
    return source[key]
  }

  return manualOverrides[language]?.[key] ?? automatic[language][key]
}

export const getTranslationStatus = (language: LanguageCode) => {
  if (language === 'tr') {
    return 'Kaynak metin'
  }

  const overrideCount = Object.keys(manualOverrides[language] ?? {}).length
  return overrideCount > 0
    ? `${overrideCount} manuel duzeltme`
    : 'Otomatik ceviri aktif'
}
